//Global imports
import {
    CButton,
    CForm,
    CFormInput,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
} from '@coreui/react';
import { useState, useEffect } from 'react';

//File imports
import { Language } from '../../../../language/English';
import { ArticleService } from '../../Api/ArticleService';
import Notification from '../../../../services/Notification/Notification';
import Logger from '../../../../services/Logger/Logger';

//Component imports
import Loader from '../../../../components/Layout/Loader';
import { HintText, LengthChecker } from '../../../../components';

interface CreateTemplateModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    mode?: 'create' | 'edit';
    articleData?:
        | {
              documentTemplateId?: number;
              name: string;
              price: number;
              outsidePrice: number;
              preparationTime: number;
          }
        | any;
}

interface FormErrors {
    name?: string;
}

const CreateArticleModal: React.FC<CreateTemplateModalProps> = ({
    visible,
    onClose,
    onSuccess = () => {},
    mode = 'create',
    articleData,
}) => {
    const notification = new Notification();
    const articleService = new ArticleService();
    const logger = new Logger();

    const [formData, setFormData] = useState({
        name: articleData?.name || '',
        isFooterLink: articleData?.isFooterLink || false,
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [showLoader, setShowLoader] = useState<boolean>(false);

    useEffect(() => {
        if (articleData) {
            setFormData({
                name: articleData?.name,
                isFooterLink: articleData?.isFooterLink,
            });
        }
    }, [articleData]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        let isValid = true;

        if (!formData.name.trim()) {
            newErrors.name = Language.NAME_REQUIRED;
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowLoader(true);
        try {
            let success = false;
            let message = '';

            if (mode === 'create') {
                const response = await articleService.create(
                    formData.name,
                    formData.isFooterLink ? 1 : 0,
                );
                success = response.success;
                message = response.message;
            } else if (mode === 'edit' && articleData?.articleId) {
                const response = await articleService.update(
                    articleData.articleId,
                    formData.name,
                    Number(formData.isFooterLink),
                );
                success = response.success;
                message = response.message;
            }

            if (success) {
                notification.success(
                    mode === 'create'
                        ? message
                        : Language.ARTICLE_UPDATED_SUCCESSFULLY,
                );
                setFormData({
                    name: '',
                    isFooterLink: false,
                });
                onClose();
                onSuccess();
            } else {
                notification.error(message);
            }
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
            logger.error(error);
        } finally {
            setShowLoader(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (errors[name as keyof FormErrors]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    return (
        <CModal visible={visible} onClose={onClose}>
            {showLoader && <Loader />}
            <CModalHeader>
                <CModalTitle>
                    {mode === 'create'
                        ? Language.CREATE_ARTICLE
                        : Language.EDIT_ARTICLE}
                </CModalTitle>
            </CModalHeader>
            <CModalBody>
                <CForm onSubmit={handleSubmit}>
                    <div className="mb-3">
                        <CFormInput
                            type="text"
                            name={Language.NAME.toLowerCase()}
                            label={Language.NAME}
                            placeholder={Language.ENTER_ARTICLE_NAME}
                            value={formData.name}
                            maxLength={100}
                            onChange={handleChange}
                            invalid={!!errors.name}
                            feedback={errors.name}
                        />
                        <LengthChecker
                            currentLength={formData.name.length}
                            maxLength={100}
                        />
                    </div>
                    <div className="d-flex align-items-center">
                        <label className="me-3">
                            {Language.IS_FOOTER_LINK}
                        </label>
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="isFooterLink"
                                checked={formData.isFooterLink}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        isFooterLink: e.target.checked,
                                    }))
                                }
                            />
                        </div>
                    </div>
                    <HintText>{Language.IS_FOOTER_LINK_HINT}</HintText>
                </CForm>
            </CModalBody>
            <CModalFooter>
                <CButton color="secondary" onClick={onClose}>
                    {Language.CANCEL}
                </CButton>
                <CButton color="primary" onClick={handleSubmit}>
                    {mode === 'create'
                        ? Language.CREATE
                        : Language.UPDATE_BUTTON}
                </CButton>
            </CModalFooter>
        </CModal>
    );
};

export default CreateArticleModal;
